import React from "react";
import LegalLayout from "@/components/LegalLayout";

/**
 * Sub-processors / Data Processing Addendum disclosure.
 *
 * Lists every third-party service that processes user data on our
 * behalf, what data flows to them, and links to their respective DPAs.
 * Required for GDPR Article 28 / CCPA service-provider transparency.
 */
export default function SubProcessors() {
  const rows = [
    {
      vendor: "Stripe, Inc.",
      purpose: "Card payments, subscriptions, marketplace escrow, KYC",
      data: "Name, email, last 4 of card, billing address, payout bank info (breeders)",
      region: "United States (with EU sub-processors per Stripe's DPA)",
      link: "https://stripe.com/legal/dpa",
    },
    {
      vendor: "Resend (Resend Inc.)",
      purpose: "Transactional email delivery (welcome, transfer invites, certificates)",
      data: "Email address, recipient name, message contents",
      region: "United States",
      link: "https://resend.com/legal/dpa",
    },
    {
      vendor: "MongoDB Atlas (MongoDB, Inc.)",
      purpose: "Primary application database",
      data: "All user-stored content (account, dogs, litters, transactions)",
      region: "United States (configurable EU region available)",
      link: "https://www.mongodb.com/legal/dpa",
    },
    {
      vendor: "Emergent (Emergent Labs, Inc.)",
      purpose: "Hosting, object storage, AI assist (LLM-generated content), Google sign-in",
      data: "Account email/name, uploaded files (photos, certificates), generated text prompts",
      region: "United States",
      link: "https://emergent.sh/legal/dpa",
    },
    {
      vendor: "Google LLC",
      purpose: "OAuth single-sign-on (only when user chooses Google login)",
      data: "Google account email, name, profile photo",
      region: "United States",
      link: "https://cloud.google.com/terms/data-processing-addendum",
    },
    {
      vendor: "Daily.co (Pluot Inc.)",
      purpose: "Live video tour rooms — short-lived audio/video calls between a buyer and a verified breeder ($35 video tour booking).",
      data: "Buyer + seller display name, room access tokens; ephemeral audio/video stream during the 15-minute call. We do not record. Daily.co retains diagnostic logs per their DPA.",
      region: "United States (with global edge network)",
      link: "https://www.daily.co/legal/dpa/",
    },
    {
      vendor: "Cloudflare, Inc.",
      purpose: "Turnstile bot/abuse protection on public forms (registration, login, password reset, contact-admin).",
      data: "IP address, browser fingerprint hash, challenge token. No content from the form is shared.",
      region: "United States + global edge",
      link: "https://www.cloudflare.com/cloudflare-customer-dpa/",
    },
    {
      vendor: "Anthropic PBC",
      purpose: "AI text generation (Claude) for the admin marketing-pack generator and the kennel-bio assistant. Routed via Emergent LLM Key — we do not hold a direct contractual relationship with Anthropic; Emergent is the contracting processor, Anthropic is the sub-processor.",
      data: "Prompts and the context required to answer them — aggregate platform statistics for the marketing generator (counts of kennels, dogs, breeds, completed transports), the user's kennel description for the bio assistant. Other users' personal data is never sent. Outputs are not retained by Anthropic for model training.",
      region: "United States",
      link: "https://www.anthropic.com/legal/privacy",
    },
    {
      vendor: "Apple Inc. (App Store & In-App Purchase)",
      purpose: "Payment processing for subscriptions purchased inside the iOS app. Apple is the merchant of record for In-App Purchases; billing runs against the user's Apple ID.",
      data: "Apple ID billing/payment data (held entirely by Apple — never shared with us), transaction identifiers, subscription product and renewal status.",
      region: "United States + global (per Apple)",
      link: "https://www.apple.com/legal/privacy/",
    },
    {
      vendor: "RevenueCat, Inc.",
      purpose: "Subscription entitlement management for the iOS app — validates Apple In-App Purchase receipts and notifies our backend which subscription a user holds (via signed webhooks).",
      data: "Internal app user ID, Apple purchase receipts / transaction identifiers, subscription product and status. No name, email, or card data is sent to RevenueCat.",
      region: "United States",
      link: "https://www.revenuecat.com/dpa",
    },
    {
      vendor: "Apple Push Notification Service",
      purpose: "Delivers web push notifications (calendar reminders, transport status, escrow milestones) to Safari and iOS users who have opted in.",
      data: "Push endpoint URL issued by APNs, public application key, encrypted payload (event title and link). Apple does not see decrypted payloads. Only used when the user has explicitly enabled push.",
      region: "United States + global edge (per Apple)",
      link: "https://www.apple.com/legal/privacy/",
    },
    {
      vendor: "Google Firebase Cloud Messaging (FCM)",
      purpose: "Delivers web push notifications to Chrome, Edge, and Android browsers that have opted in.",
      data: "FCM push endpoint, public application key, encrypted payload. Only used when the user has explicitly enabled push.",
      region: "United States",
      link: "https://firebase.google.com/support/privacy",
    },
    {
      vendor: "Mozilla autopush",
      purpose: "Delivers web push notifications to Firefox users who have opted in.",
      data: "autopush endpoint, public application key, encrypted payload. Only used when the user has explicitly enabled push.",
      region: "United States",
      link: "https://www.mozilla.org/en-US/privacy/websites/",
    },
  ];

  return (
    <LegalLayout
      slug="sub-processors"
      eyebrow="Compliance"
      title="Sub-Processors & Data Flow"
      lastUpdated="May 26, 2026 · Reviewed by counsel"
    >
      <p>
        We use a small set of vetted vendors to deliver the registry. Each
        vendor processes only the data necessary for the function described
        below and has signed a Data Processing Agreement with us. This page
        is the canonical list — when we add or remove a sub-processor we
        update it here within 30 days.
      </p>

      <div className="not-prose overflow-x-auto mt-8">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#FBF7EC] text-[#8A6A00]">
              <th className="text-left p-3 font-semibold">Vendor</th>
              <th className="text-left p-3 font-semibold">Purpose</th>
              <th className="text-left p-3 font-semibold">Data shared</th>
              <th className="text-left p-3 font-semibold">Region</th>
              <th className="text-left p-3 font-semibold">DPA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.vendor} className="border-b border-[#E5E5E5]/60 align-top" data-testid={`subprocessor-${r.vendor.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="p-3 font-semibold">{r.vendor}</td>
                <td className="p-3 text-[#3F3F3F]">{r.purpose}</td>
                <td className="p-3 text-[#3F3F3F]">{r.data}</td>
                <td className="p-3 text-[#3F3F3F]">{r.region}</td>
                <td className="p-3">
                  <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-crimson underline underline-offset-2">
                    View
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>International transfers</h2>
      <p>
        We are based in the United States. EU/UK personal data is transferred
        to the United States under the EU-US Data Privacy Framework and
        Standard Contractual Clauses (SCCs) where applicable. Each
        sub-processor above maintains its own SCC framework as documented in
        their DPA.
      </p>

      <h2>Tax / financial flow</h2>
      <p>
        All buyer card payments are processed by Stripe. For marketplace
        purchases (one breeder selling a dog to a buyer), funds route through{" "}
        <strong>Stripe Connect Express</strong> — Stripe holds the funds in
        the seller's connected account, then settles them to the seller's
        bank. Armstrong K9 Registry never takes beneficial
        ownership of the buyer's payment for the dog. The seller remains the
        merchant of record for the sale and is responsible for sales tax,
        income tax, and 1099-K reporting in their jurisdiction. Stripe
        issues the 1099-K (where applicable) directly to the seller's
        Connect account; the Registry is not part of that filing.
      </p>

      <h2>Object to a new sub-processor</h2>
      <p>
        If you object to a new sub-processor, contact us at{" "}
        <a href="mailto:privacy@armstrongk9registry.com">privacy@armstrongk9registry.com</a>{" "}
        within 30 days of this page being updated. We will work in good faith
        to find an alternative; if none is feasible you may terminate your
        subscription and receive a pro-rated refund.
      </p>
    </LegalLayout>
  );
}
